import { Box, CircularProgress, Typography } from '@mui/material';
import React, { forwardRef, useEffect, useState } from 'react';
import type { ContentItem } from '../../models/ContentManagementInterface';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import type FundOpportunity from '../../models/FundOpportunity';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import BackendService from '../../services/admin/BackendService';
import AddFootnoteToolbarButton from '../admin/newContentManagement/AddFootnoteToolbarButton';
import BlockDropZone from '../admin/newContentManagement/BlockDropZone';
import BlockPalette from '../admin/newContentManagement/BlockPalette';
import { BlockPaletteDragProvider } from '../admin/newContentManagement/blockPaletteDragContext';
import ContentBlockEditor from '../admin/newContentManagement/ContentBlockEditor';
import ContentBlockWrapper from '../admin/newContentManagement/ContentBlockWrapper';
import { EditorSidebarProvider } from '../admin/newContentManagement/editorSidebarContext';
import LibraryPanel from '../admin/newContentManagement/LibraryPanel';
import {
  blockSupportsFootnotes,
  createContentBlock,
  isFootnotesOpen,
} from '../admin/newContentManagement/newContentManagementEditorUtils';
import type {
  PageMode,
  SaveStatus,
} from '../admin/newContentManagement/newContentManagementEditorTypes';
import type { ImageCardsCropMeta } from '../admin/newContentManagement/useImageCardsSplit';
import type { SimplifiedFundUpdateEditorHandle } from './SimplifiedFundUpdateEditor.types';
import { useSimplifiedFundUpdateEditor } from './useSimplifiedFundUpdateEditor';

interface SimplifiedFundUpdateEditorProps {
  update: AdminFundUpdateEntry;
  fundOpportunity: FundOpportunity;
  onBusyChange?: (busy: boolean) => void;
  /**
   * Reports this editor's save/preview state upward so a page-level toolbar
   * (shared with whichever update is currently "open") can drive Save and the
   * View toggle — this editor no longer renders its own toolbar.
   */
  onToolbarStateChange?: (state: {
    saveStatus: SaveStatus;
    pageMode: PageMode;
    setPageMode: (mode: PageMode) => void;
    saveNow: () => void;
    toolbarRef: React.RefObject<HTMLDivElement | null>;
  }) => void;
  /**
   * The Fund Highlight Card belongs to this update's content, so it's up to
   * the caller to decide whether/how to show it — rendered only once there's
   * at least one content block, and only in the edit view (not previews).
   */
  renderAfterContent?: (info: { hasContent: boolean; pageMode: PageMode }) => React.ReactNode;
  /**
   * This editor owns the update's live, edited content — the list of updates
   * the rest of the panel reads from (for the Fund Highlight Card's
   * auto-inferred preview, among other things) only has a snapshot from when
   * it was loaded. Reports every change so the caller can keep that snapshot
   * in sync instead of it silently going stale as the admin edits.
   */
  onContentChange?: (content: ContentItem[]) => void;
}

interface EditorCanvasProps {
  content: ContentManagementInterface;
  update: AdminFundUpdateEntry;
  fundOpportunity: FundOpportunity;
  backendService: BackendService;
  updateBlock: (index: number, updatedBlock: ContentItem) => void;
  moveBlock: (index: number, direction: 'up' | 'down') => void;
  removeBlock: (index: number) => void;
  addBlock: (index: number, type: ContentItem['type'], where: 'before' | 'after') => void;
  insertSections: (index: number, sections: ContentItem[]) => void;
  appendImportedSections: (
    sections: ContentItem[],
    cropMeta?: ImageCardsCropMeta | null,
    atIndex?: number,
  ) => void;
  cardsAdjustByBlockId: Record<string, ImageCardsCropMeta>;
  clearCardsAdjustForBlock: (blockId: string) => void;
  footnotesOpenByBlockId: Record<string, boolean>;
  toggleFootnotes: (blockId: string, currentlyOpen: boolean) => void;
}

function LoadingEditor() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight={240} gap={2}>
      <CircularProgress size={24} />
      <Typography>Loading...</Typography>
    </Box>
  );
}

/** One block on the canvas: its drop zone, toolbar (with the footnote toggle) and editor. */
function FundUpdateCanvasBlock({
  block,
  index,
  total,
  update,
  fundId,
  backendService,
  canvas,
  autoImportFile,
  onSwapToImageCards,
  onAutoImportConsumed,
}: {
  block: ContentItem;
  index: number;
  total: number;
  update: AdminFundUpdateEntry;
  fundId: string;
  backendService: BackendService;
  canvas: EditorCanvasProps;
  autoImportFile?: File;
  onSwapToImageCards: (file: File) => void;
  onAutoImportConsumed: () => void;
}) {
  const footnotesOpen = isFootnotesOpen(block, canvas.footnotesOpenByBlockId);
  return (
    <>
      <BlockDropZone index={index} where="before" onDrop={canvas.addBlock} />
      <ContentBlockWrapper
        index={index}
        total={total}
        blockType={block.type}
        blockId={block.id}
        onMove={canvas.moveBlock}
        onRemove={canvas.removeBlock}
        onAdd={canvas.addBlock}
        hideInsertSlots
        toolbarAction={
          blockSupportsFootnotes(block.type) ? (
            <AddFootnoteToolbarButton
              open={footnotesOpen}
              onClick={() => canvas.toggleFootnotes(block.id, footnotesOpen)}
            />
          ) : undefined
        }
      >
        <ContentBlockEditor
          block={block}
          onChange={(updated) => canvas.updateBlock(index, updated)}
          modelId={update.externalId}
          modelType="FundUpdate"
          fundOpportunityId={fundId}
          backendService={backendService}
          onInsertSections={(sections) => canvas.insertSections(index, sections)}
          onSwapToImageCards={onSwapToImageCards}
          autoImportFile={autoImportFile}
          onAutoImportConsumed={onAutoImportConsumed}
          primeCardsAdjust={canvas.cardsAdjustByBlockId[block.id]}
          onPrimeCardsAdjustConsumed={() => canvas.clearCardsAdjustForBlock(block.id)}
          footnotesOpen={footnotesOpen}
        />
      </ContentBlockWrapper>
    </>
  );
}

/** Drag-and-drop content canvas — same CONTENT/MEDIA sidebar and block list as the Fund Information editor. */
function FundUpdateEditCanvas(canvasProps: EditorCanvasProps) {
  const { content, update, fundOpportunity, backendService } = canvasProps;
  const { addBlock, insertSections, appendImportedSections } = canvasProps;
  const [pendingImageImport, setPendingImageImport] = useState<{
    index: number;
    file: File;
  } | null>(null);
  const blocks = content.content;
  const fundId = fundOpportunity.id;

  return (
    <>
      <BlockPalette
        // Keep the sidebar closed initially on this busy page.
        emptyCanvas={false}
        fundOpportunityId={fundId}
        backendService={backendService}
        modelType="FundUpdate"
        modelId={update.externalId}
      />
      <Box>
        <BlockDropZone
          index={-1}
          where="after"
          onDrop={addBlock}
          emptyState
          smartPaste={{
            modelId: update.externalId,
            modelType: 'FundUpdate',
            backendService,
            onResolved: (sections, cropMeta) => appendImportedSections(sections, cropMeta, 0),
          }}
        />
        {blocks.map((block, index) => (
          <FundUpdateCanvasBlock
            key={block.id}
            block={block}
            index={index}
            total={blocks.length}
            update={update}
            fundId={fundId}
            backendService={backendService}
            canvas={canvasProps}
            autoImportFile={
              pendingImageImport?.index === index ? pendingImageImport.file : undefined
            }
            onSwapToImageCards={(file) => {
              insertSections(index, [
                createContentBlock('ImageCarousel', update.externalId, 'FundUpdate'),
              ]);
              setPendingImageImport({ index, file });
            }}
            onAutoImportConsumed={() => setPendingImageImport(null)}
          />
        ))}
        {blocks.length > 0 && (
          <BlockDropZone index={blocks.length - 1} where="after" onDrop={addBlock} />
        )}
      </Box>
    </>
  );
}

function PreviewContent({
  content,
  update,
  fundOpportunity,
  pageMode,
  backendService,
  ...editProps
}: EditorCanvasProps & { pageMode: PageMode }) {
  // Preview Web / Preview App are handled page-level (see usePageLevelPreviewMode) —
  // this editor is unmounted while either is showing, so it only ever sees edit/library.
  if (pageMode === 'library') {
    return <LibraryPanel fundOpportunityId={fundOpportunity.id} backendService={backendService} />;
  }
  return (
    <FundUpdateEditCanvas
      content={content}
      update={update}
      fundOpportunity={fundOpportunity}
      backendService={backendService}
      {...editProps}
    />
  );
}

/** Reports the toolbar state and live content upward to the page that owns them. */
function useReportEditorState(
  editor: ReturnType<typeof useSimplifiedFundUpdateEditor>,
  onToolbarStateChange: SimplifiedFundUpdateEditorProps['onToolbarStateChange'],
  onContentChange: SimplifiedFundUpdateEditorProps['onContentChange'],
) {
  useEffect(() => {
    onToolbarStateChange?.({
      saveStatus: editor.saveStatus,
      pageMode: editor.pageMode,
      setPageMode: editor.setPageMode,
      saveNow: () => void editor.saveNow(),
      toolbarRef: editor.toolbarRef,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.saveStatus, editor.pageMode, editor.saveNow]);

  useEffect(() => {
    if (editor.content) onContentChange?.(editor.content.content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.content]);
}

const SimplifiedFundUpdateEditor = forwardRef<
  SimplifiedFundUpdateEditorHandle,
  SimplifiedFundUpdateEditorProps
>(
  (
    {
      update,
      fundOpportunity,
      onBusyChange,
      onToolbarStateChange,
      renderAfterContent,
      onContentChange,
    },
    ref,
  ) => {
    const editor = useSimplifiedFundUpdateEditor(update, fundOpportunity, ref, onBusyChange);
    useReportEditorState(editor, onToolbarStateChange, onContentChange);

    if (editor.loading) return <LoadingEditor />;
    if (!editor.content) return null;

    const previewAndHighlight = (
      <>
        <PreviewContent
          content={editor.content}
          update={update}
          fundOpportunity={fundOpportunity}
          pageMode={editor.pageMode}
          backendService={editor.backendService}
          updateBlock={editor.updateBlock}
          moveBlock={editor.moveBlock}
          removeBlock={editor.removeBlock}
          addBlock={editor.addBlock}
          insertSections={editor.insertSections}
          appendImportedSections={editor.appendImportedSections}
          cardsAdjustByBlockId={editor.cardsAdjustByBlockId}
          clearCardsAdjustForBlock={editor.clearCardsAdjustForBlock}
          footnotesOpenByBlockId={editor.footnotesOpenByBlockId}
          toggleFootnotes={editor.toggleFootnotes}
        />
        {renderAfterContent?.({
          hasContent: editor.content.content.length > 0,
          pageMode: editor.pageMode,
        })}
      </>
    );

    // The caller owns the bordered panel; blocks need only internal spacing.
    return (
      <BlockPaletteDragProvider>
        <EditorSidebarProvider onImportSections={editor.appendImportedSections}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              // Each content block already carries its own bottom margin
              // (ContentBlockWrapper's mb:2) — a full gap here on top of that
              // doubled the visible space before the Fund Highlight Card.
              gap: 1,
            }}
          >
            {previewAndHighlight}
          </Box>
        </EditorSidebarProvider>
      </BlockPaletteDragProvider>
    );
  },
);

SimplifiedFundUpdateEditor.displayName = 'SimplifiedFundUpdateEditor';

export type { SimplifiedFundUpdateEditorHandle };
export default SimplifiedFundUpdateEditor;
